<template>
    <div class="grid items-center border-t border-gray-100 bg-gray-50" :style="gridStyle">
        <div class="flex flex-col gap-0.5 py-3 pr-6 pl-12">
            <div class="text-sm font-medium text-body">{{ asset.label }}</div>
            <div v-if="asset.locationLabel" class="text-xs text-muted">{{ asset.locationLabel }}</div>
        </div>
        <div
            v-for="(year, index) in years"
            :key="year"
            class="flex items-center justify-around py-2"
            :class="[index > 0 ? 'border-l border-gray-200' : '', showLabels ? 'gap-1 px-2' : 'gap-0.5 px-1']"
        >
            <TimelineCell v-for="cell in alignedCellsForYear(year)" :key="cell.periodStart" :cell="cell" :show-label="showLabels" />
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import TimelineCell from './TimelineCell.vue';
import { ROW_HORIZONTAL_MARGIN_PX, rowGridTemplate } from './timelineLayout';

const props = defineProps({
    asset: {
        type: Object,
        required: true,
    },
    /**
     * Canonical period list from the parent equipment row. Every asset row
     * renders one slot per entry here — using the asset's matching cell when
     * present, otherwise an invisible placeholder — so cells stay vertically
     * aligned with the equipment-row aggregate even when an asset only has
     * cadence cells for a subset of periods (e.g. came online mid-contract).
     */
    canonicalCells: {
        type: Array,
        default: () => [],
    },
    years: {
        type: Array,
        required: true,
    },
    /** Whether per-cell month labels are shown (false for dense cadences). */
    showLabels: {
        type: Boolean,
        default: true,
    },
});

const ownCellsByPeriodStart = computed(() => {
    const map = new Map();
    for (const cell of props.asset.cells ?? []) {
        map.set(cell.periodStart, cell);
    }
    return map;
});

const alignedCellsByYear = computed(() => {
    const map = new Map();
    for (const canonical of props.canonicalCells ?? []) {
        const own = ownCellsByPeriodStart.value.get(canonical.periodStart);
        const cell = own ?? {
            status: 'placeholder',
            periodStart: canonical.periodStart,
            periodEnd: canonical.periodEnd,
            year: canonical.year,
            workOrderId: null,
        };
        if (!map.has(canonical.year)) {
            map.set(canonical.year, []);
        }
        map.get(canonical.year).push(cell);
    }
    return map;
});

const alignedCellsForYear = (year) => alignedCellsByYear.value.get(year) ?? [];

const gridStyle = computed(() => ({
    gridTemplateColumns: rowGridTemplate(props.years.length),
    marginLeft: `${ROW_HORIZONTAL_MARGIN_PX}px`,
    marginRight: `${ROW_HORIZONTAL_MARGIN_PX}px`,
}));
</script>
